import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import CustomSlider from '../../Shared/components/controls/Slider/index.js';
import CustomTextField from '../../Shared/components/controls/TextField/index.js';

const minDistance = 1;

const SliderRange = ({ props, t, reset }) => {
  const { field, onChange, tracker, errors } = props;
  const valueField = field.valueField;
  const model = field.model;
  const double = !!model.multiRange;

  const [value, setValue] = useState(getInitialValue(model));
  const [firstValid, setFirstValid] = useState(true);
  const [secondValid, setSecondValid] = useState(true);

  //With this onChange is called only after 500ms of user inactivity (optimization)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!firstValid || !secondValid) {
        onChange(valueField.name, value, false, [`${model.title} ${t('sliderExceedRange')}`]);
      } else {
        onChange(valueField.name, value, true, []);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [value, firstValid, secondValid]);

  // validation
  useEffect(() => {
    if (double) {
      setFirstValid(value[0] >= model.min && value[0] < value[1] && value[0] < model.max);
      setSecondValid(value[1] <= model.max && value[1] > value[0] && value[1] > model.min);
    } else {
      setFirstValid(value >= model.min && value <= model.max);
    }
  }, [value]);

  useEffect(() => {
    setValue(getInitialValue(model));
  }, [reset]);

  const handleChange = (newValue, index) => {
    const number = +newValue;
    if (!isNaN(number)) {
      if (double) {
        if (index === 0) {
          setValue((prev) => [number, prev[1]]); //[1,100]
        } else {
          setValue((prev) => [prev[0], number]);
        }
      } else {
        // noinspection JSCheckFunctionSignatures
        setValue(number);
      }
    }
  };

  const markPointsEvery = useMemo(() => {
    const { displayDivisions, divisions } = model;
    if (!displayDivisions || !isDefined(divisions)) return null;
    return Math.round((model.max - model.min) / divisions);
  }, [model?.displayDivisions, model?.divisions]);

  return (
    <div className="form-element form-slider">
      <h5 className="form-slider-title">{model.title}</h5>
      <RichText field={{ value: model.description }} className="form-slider-text" />
      <CustomSlider
        value={value} //controlled
        setValue={setValue}
        minValue={model.min}
        maxValue={model.max}
        double={double}
        markPointsEvery={markPointsEvery}
        step={model.step || 1}
        minDistance={minDistance}
        className="form-slider-slider"
        id={valueField.id}
        name={valueField.name}
      />
      <div className="form-slider-inputs">
        {double ? (
          <>
            <CustomTextField
              value={value[0]}
              setValue={(newV) => handleChange(newV, 0)}
              label={model.label1}
              placeholder={model.placeholder1}
              prefix={model.prefix1}
              valid={firstValid}
              helperText={firstValid ? '' : t('sliderExceedRange')}
              onFocus={() => tracker.onFocusField(field, value)}
              onBlur={() => tracker.onBlurField(field, value, errors)}
            />
            <CustomTextField
              value={value[1]}
              setValue={(newV) => handleChange(newV, 1)}
              label={model.label2}
              placeholder={model.placeholder2}
              prefix={model.prefix2}
              valid={secondValid}
              helperText={secondValid ? '' : t('sliderExceedRange')}
              onFocus={() => tracker.onFocusField(field, value)}
              onBlur={() => tracker.onBlurField(field, value, errors)}
            />
          </>
        ) : (
          <CustomTextField
            value={value}
            setValue={(newV) => handleChange(newV, 0)}
            label={model.label1}
            placeholder={model.placeholder1}
            prefix={model.prefix1}
            valid={firstValid}
            helperText={firstValid ? '' : t('sliderExceedRange')}
            onFocus={() => tracker.onFocusField(field, value)}
            onBlur={() => tracker.onBlurField(field, value, errors)}
          />
        )}
      </div>
    </div>
  );
};

const isDefined = (value) => value !== null && value !== undefined;

const getInitialValue = (model) => {
  const { value, defaultValue1, defaultValue2 } = model;
  const double = !!model.multiRange;
  if (double) {
    let left = model.min;
    let right = model.max;
    if (isDefined(defaultValue1)) left = defaultValue1;
    if (value && isDefined(value[0])) left = value[0];
    if (isDefined(defaultValue2)) right = defaultValue2;
    if (value && isDefined(value[1])) right = value[1];
    return [left, right];
  } else {
    if (isDefined(value)) return value;
    if (isDefined(defaultValue1)) return defaultValue1;
    return model.min;
  }
};

export default SliderRange;
